import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { COLOR } from '@src/styles/color';
import { TYPE_UPLOAD } from '@src/constants/upload';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { FEATURE } from '@src/constants/package';
import { PROJECT_PRODUCT } from '@src/constants/project';
import { MOE_SURFACE } from '@src/constants/moengage';
import { ERROR_CODE } from '@src/errors/code';
import route from '@src/constants/route';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import CrownImg from '@src/assets/images/crown.png';
import {
  getSlideCountFromPptx,
  calculatePresentationCredit,
} from '@src/utils/presentation';
import { getAudioDuration } from '@src/services/audio';
import { formatFileSize } from '@src/utils/file';
import {
  AUDIO_ONLY_EXTENSIONS,
  DEFAULT_TRANSCRIPT_MODEL,
  MAX_AUDIO_DURATION_SECONDS,
  MAX_AUDIO_SIZE_BYTES,
  SUBTITLE_SRT_MAX_SIZE_BYTES,
  SUBTITLE_MAX_TOTAL_CHARS,
} from '@src/constants/transcript';
import { calculateTranscriptCredit } from '@src/services/transcript';
import {
  getMinPackageNameForFeature,
  hasActiveFeature,
} from '@src/services/package';
import useCreditsLocked from '@src/hooks/useCreditsLocked';
import useTotalCredits from '@src/hooks/useTotalCredits';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import useAINarrationUpgrade from '@src/hooks/useAINarrationUpgrade';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import Typography from '@src/new-components/Typography';
import PopupUpPkgUseRewrite from '@src/containers/ProjectDetail/Dubbing/Content/PopupUpPkgUseRewrite';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import usePresentationAIModels from '@src/hooks/usePresentationAIModels';
import useTranscriptAudioModels from '@src/hooks/useTranscriptAudioModels';
import { useCreateProjectFromInput } from '@src/hooks/useCreateProjectFromInput';
import { LANGUAGE } from '@src/constants';
import actions from '@src/redux/actions';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import NavigationBlocker from '@src/new-components/NavigationBlocker';
import ProjectFileDropzone from '@src/new-components/DropZone/ProjectFileDropzone';
import {
  FILE_VARIANTS,
  buildDefaultTitle,
  buildEmptyDubbingBlocks,
  SUBTITLE_MODE,
  isAutoSubtitleMode,
  processFileVariant,
  validateVideoSubtitleSrt,
  buildVideoSrtCreatePayload,
} from '@src/services/projectInput';
import { resolveAiOptionsTipText } from '@src/services/project';
import ProjectAIOptions from './ProjectAIOptions';

const UploadProjectFilePopup = ({ open, onClose, variant }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { languagesV3 } = useSelector((state) => state.language);
  const { getFeatureValue } = useFeatureFlags();
  const { createProject, finalizeProjectDefault } = useCreateProjectFromInput();
  const { isCreditsLocked } = useCreditsLocked();

  const config = FILE_VARIANTS[variant];

  const userFeatures = user?.studioUsageOptions?.features || [];
  const hasAIPresentationAccess = hasActiveFeature({
    user,
    feature: FEATURE.AI_GENERATE_NARRATION,
  });
  const hasTranscriptPermission = userFeatures.includes(
    FEATURE.TRANSCRIPT_DUBBING,
  );

  const { totalCredits } = useTotalCredits();
  const { packages } = useMappingPackage();

  const presentationLLMEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_LLM,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  const presentationSlideV2Enabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_SLIDE_V2,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const baseLanguages = useMemo(() => {
    if (!Array.isArray(languagesV3)) return [];
    return [...languagesV3]
      .filter((item) => item.value !== 'multilingual')
      .sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        const labelA = a.label?.[currentLang] || '';
        const labelB = b.label?.[currentLang] || '';
        return labelA.localeCompare(labelB);
      })
      .map((item) => ({
        ...item,
        label: item.label?.[currentLang] || item.label?.[LANGUAGE.EN],
      }));
  }, [languagesV3, currentLang]);

  const pptx = usePresentationAIModels();
  const audio = useTranscriptAudioModels();
  const { models = [], getDescription: getModelDescription } =
    {
      [TYPE_UPLOAD.PPTX]: pptx,
      [TYPE_UPLOAD.AUDIO]: audio,
      [TYPE_UPLOAD.VIDEO_DUBBING]: audio,
    }[variant] || {};
  const isVideoDubbing = variant === TYPE_UPLOAD.VIDEO_DUBBING;
  // Both AUDIO (transcript) and VIDEO_DUBBING share the same backend pipeline,
  // models, and credit logic — treat them together everywhere except the
  // dropzone file types and the popup title.
  const isAudio = variant === TYPE_UPLOAD.AUDIO || isVideoDubbing;
  const isPptx = variant === TYPE_UPLOAD.PPTX;
  const showAIOptions =
    config?.withAI && (variant !== TYPE_UPLOAD.PPTX || presentationLLMEnabled);

  const videoDubbingProjectEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_PROJECT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  // The auto-detect-voice switch is part of the same rollout as the video
  // dubbing card. Until that flag is on, the AUDIO popup keeps its legacy
  // layout (language/model/tip always visible, no switch).
  const showAutoDetectSwitch = isAudio && videoDubbingProjectEnabled;

  const videoDubbingSrtUploadEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_SRT_UPLOAD,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  // AIV-12773 US-03: for the video dubbing variant, the auto-detect switch is
  // replaced by a subtitle-method dropdown. Gated by BOTH the video dubbing
  // rollout flag and the SRT-upload flag so the legacy switch stays untouched
  // when either is off.
  const showSubtitleMethodDropdown =
    isVideoDubbing && showAutoDetectSwitch && videoDubbingSrtUploadEnabled;

  // Explicit subtitle method (video dubbing US-03). SRT can't be represented by
  // the `aiConfig.enabled` boolean (both SRT and NONE skip STT), so the dropdown
  // selection lives in its own state; `enabled` is kept in sync for the shared
  // credit/guard/submit branches.
  const [subtitleMode, setSubtitleMode] = useState(SUBTITLE_MODE.AUTO);
  const isSrtMode =
    showSubtitleMethodDropdown && subtitleMode === SUBTITLE_MODE.SRT;
  const [srtFile, setSrtFile] = useState(null);
  const [srtDetails, setSrtDetails] = useState(undefined);
  const [srtError, setSrtError] = useState('');
  const [srtErrorDetail, setSrtErrorDetail] = useState('');
  const [computingSrt, setComputingSrt] = useState(false);

  const [file, setFile] = useState(null);
  // mp3/wav cannot be processed by the video-only pipeline, so the auto-detect
  // toggle is forced ON and disabled for those formats.
  const fileExt = useMemo(
    () => file?.name?.split?.('.')?.pop()?.toLowerCase() || '',
    [file],
  );
  const forceAutoDetect = AUDIO_ONLY_EXTENSIONS.includes(fileExt);
  const [fileDetails, setFileDetails] = useState(undefined);
  const [computingDetails, setComputingDetails] = useState(false);
  const [error, setError] = useState('');
  // Optional HTML detail accompanying SRT validation errors (line numbers /
  // timestamps of the failing block). Rendered via dangerouslySetInnerHTML.
  const [errorDetail, setErrorDetail] = useState('');
  const [aiConfig, setAiConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(false);
  const [pptxSlideCount, setPptxSlideCount] = useState(0);
  const [duration, setDuration] = useState(0);
  const [openPopupUpPkg, setOpenPopupUpPkg] = useState(false);
  const [openDialogNotEnoughCredits, setOpenDialogNotEnoughCredits] =
    useState(false);

  // AUDIO permission: model name in the feature flag is the same string as
  // the corresponding package feature (e.g. "vbee-transcribe-flash"). User
  // either skips model selection (always allowed) or must own that feature.
  const selectedAudioModelValue = isAudio
    ? aiConfig?.model?.value || aiConfig?.model?.model || ''
    : '';
  const hasSelectedModelPermission =
    !selectedAudioModelValue || userFeatures.includes(selectedAudioModelValue);

  const missingFeature = !hasTranscriptPermission
    ? FEATURE.TRANSCRIPT_DUBBING
    : selectedAudioModelValue;
  const upgradePkgName = useMemo(
    () =>
      getMinPackageNameForFeature({
        packages,
        feature: missingFeature,
        language: currentLang,
      }),
    [packages, missingFeature, currentLang],
  );

  const isPptxAccessLocked =
    isPptx && presentationLLMEnabled && !hasAIPresentationAccess;

  const {
    upgradePkgName: pptxUpgradePkgName,
    targetPackageCode: pptxTargetPackageCode,
  } = useAINarrationUpgrade();

  // PPTX-only: AI is "active" only when the LLM flag is on, the user has
  // package access, runtime models exist, and the user toggled the switch on.
  const isAIContentEnabled =
    isPptx &&
    presentationLLMEnabled &&
    hasAIPresentationAccess &&
    models.length > 0 &&
    Boolean(aiConfig?.enabled);

  // AUDIO supports auto-detect and lets each model whitelist a subset of languages
  // via `supportedLanguage`. PPTX uses the full language list with a 'vi' default.
  const languages = useMemo(() => {
    if (!isAudio) return baseLanguages;
    const autoOption = { label: t('autoDetect'), value: 'auto' };
    const supported = aiConfig?.model?.supportedLanguage;
    if (Array.isArray(supported) && supported.length === 0) return [autoOption];
    const filtered = !Array.isArray(supported)
      ? baseLanguages
      : baseLanguages.filter((item) => supported.includes(item.value));
    return [autoOption, ...filtered];
  }, [isAudio, baseLanguages, aiConfig?.model, t]);

  // Names for the "Chỉ hỗ trợ ngôn ngữ: …" caption under the language dropdown.
  // Only models that whitelist a finite set say anything — PPTX carries no
  // `supportedLanguage`, and an empty whitelist means auto-detect only, so both
  // leave the caption hidden. Derived from `languages` so the names match the
  // dropdown's localization and ordering exactly.
  const supportedLanguageLabels = useMemo(() => {
    const supported = aiConfig?.model?.supportedLanguage;
    if (!Array.isArray(supported) || supported.length === 0) return [];
    return languages
      .filter((item) => item.value !== 'auto')
      .map((item) => item.label);
  }, [aiConfig?.model, languages]);

  // Subtitle-method options for the video dubbing dropdown (US-03):
  // auto-detect (STT), upload an existing .srt, or none.
  const subtitleModeOptions = useMemo(
    () => [
      { value: SUBTITLE_MODE.AUTO, label: t('subtitleModeGenerateWithAI') },
      { value: SUBTITLE_MODE.SRT, label: t('subtitleModeUploadSrt') },
      { value: SUBTITLE_MODE.NONE, label: t('subtitleModeNone') },
    ],
    [t],
  );

  // Reset state when popup closes.
  useEffect(() => {
    if (open) return;
    setFile(null);
    setFileDetails(undefined);
    setComputingDetails(false);
    setError('');
    setErrorDetail('');
    setAiConfig(null);
    setProcessing(false);
    setOpenDialogLockedCycle(false);
    setPptxSlideCount(0);
    setDuration(0);
    setOpenPopupUpPkg(false);
    setOpenDialogNotEnoughCredits(false);
    setSubtitleMode(SUBTITLE_MODE.AUTO);
    setSrtFile(null);
    setSrtDetails(undefined);
    setSrtError('');
    setSrtErrorDetail('');
    setComputingSrt(false);
  }, [open]);

  // PPTX: track slide count locally for the credit cost tooltip. Independent
  // of `computeDetails` so we can read it without touching fileDetails shape.
  useEffect(() => {
    if (!open || variant !== TYPE_UPLOAD.PPTX || !file) {
      setPptxSlideCount(0);
      return null;
    }
    let cancelled = false;
    const fetchSlideCount = async () => {
      try {
        const count = await getSlideCountFromPptx(file);
        if (!cancelled) setPptxSlideCount(count || 0);
      } catch {
        if (!cancelled) setPptxSlideCount(0);
      }
    };
    fetchSlideCount();
    return () => {
      cancelled = true;
    };
  }, [open, variant, file]);

  // Warn before unload while a submit is in progress.
  useEffect(() => {
    if (!processing) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [processing]);

  // Init aiConfig when popup opens; switch ON even without access — Continue paywalls
  useEffect(() => {
    if (!open || !showAIOptions) return;
    // AUDIO / VIDEO_DUBBING default to the pro model when the flag exposes it.
    const defaultModel =
      (isAudio &&
        models.find(
          (m) => (m.model || m.value) === DEFAULT_TRANSCRIPT_MODEL,
        )) ||
      models[0] ||
      null;
    setAiConfig({
      enabled: true,
      language: 'vi',
      model: defaultModel,
    });
    // Re-run when models populate; depending on length keeps the dep stable.
  }, [open, isPptx, isAudio, showAIOptions, models.length]);

  // mp3/wav inputs must run through the audio transcript pipeline — keep
  // auto-detect ON whenever such a file is selected, regardless of prior toggle
  // state.
  useEffect(() => {
    if (!showAutoDetectSwitch || !forceAutoDetect) return;
    setAiConfig((prev) =>
      prev && !prev.enabled ? { ...prev, enabled: true } : prev,
    );
  }, [showAutoDetectSwitch, forceAutoDetect]);

  // AUDIO: when the model changes, drop a language that the new model no longer
  // supports — fall back to auto-detect so the form stays valid.
  useEffect(() => {
    if (!isAudio || !aiConfig || !languagesV3?.length) return;
    const isValid = languages.some((o) => o.value === aiConfig.language);
    if (!isValid) setAiConfig((prev) => ({ ...prev, language: 'auto' }));
  }, [isAudio, languages, aiConfig, languagesV3?.length]);

  // Fetch language list once when popup opens.
  useEffect(() => {
    if (!open) return;
    if (!languagesV3?.length) {
      dispatch(actions.language.getLanguagesV3());
    }
  }, [open, languagesV3, dispatch]);

  const handleFiles = useCallback(
    async (files) => {
      const picked = files?.[0];
      if (!picked || !config) return;
      setError('');
      setErrorDetail('');

      const ext = picked.name?.split?.('.')?.pop()?.toLowerCase();
      if (config.fileType?.length && !config.fileType.includes(ext)) {
        setError(t('invalidFormat'));
        return;
      }

      if (config.maxSize && picked.size > config.maxSize) {
        setError(t('oversizeError'));
        return;
      }

      if (config.validate) {
        // Validators may return a plain string OR `{ message, detail }` (SRT).
        const result = await config.validate(picked);
        if (result) {
          if (typeof result === 'string') {
            setError(result);
          } else {
            setError(result.message || t('invalidFormat'));
            setErrorDetail(result.detail || '');
          }
          setDuration(0);
          return;
        }
      }

      // AUDIO: capture duration for credit calc. `validateAudioFile` already
      // enforced the duration cap, so this is just bookkeeping for the tooltip
      // and source metadata.
      if (isAudio) {
        const seconds = await getAudioDuration(picked).catch(() => 0);
        setDuration(seconds || 0);
      }

      setFile(picked);

      if (config.computeDetails) {
        setComputingDetails(true);
        try {
          const details = await config.computeDetails(picked, t);
          setFileDetails(details);
        } finally {
          setComputingDetails(false);
        }
      }
    },
    [config, variant, t],
  );

  const handleRemove = () => {
    setFile(null);
    setFileDetails(undefined);
    setComputingDetails(false);
    setError('');
    setErrorDetail('');
    setDuration(0);
  };

  const clearSrt = () => {
    setSrtFile(null);
    setSrtDetails(undefined);
    setSrtError('');
    setSrtErrorDetail('');
    setComputingSrt(false);
  };

  // Switch subtitle method: keep `aiConfig.enabled` in sync (AUTO → on) and
  // drop any picked .srt when leaving the SRT method (per spec).
  const handleSubtitleModeChange = (mode) => {
    setSubtitleMode(mode);
    setAiConfig((prev) => ({ ...prev, enabled: isAutoSubtitleMode(mode) }));
    if (mode !== SUBTITLE_MODE.SRT) clearSrt();
  };

  const handleSrtFiles = async (files) => {
    const picked = files?.[0];
    if (!picked) return;
    setSrtError('');
    setSrtErrorDetail('');

    if (picked.size > SUBTITLE_SRT_MAX_SIZE_BYTES) {
      setSrtError(t('oversizeError'));
      return;
    }

    const result = await validateVideoSubtitleSrt(picked);
    if (result) {
      setSrtError(result.message || t('invalidFormat'));
      setSrtErrorDetail(result.detail || '');
      return;
    }

    setSrtFile(picked);
    setComputingSrt(true);
    try {
      const details = await FILE_VARIANTS[TYPE_UPLOAD.FILE].computeDetails(
        picked,
        t,
      );
      setSrtDetails(details);
    } finally {
      setComputingSrt(false);
    }
  };

  const selectedAudioModelData = useMemo(() => {
    if (!isAudio) return null;
    return models.find((m) => (m.model || m.value) === selectedAudioModelValue);
  }, [variant, models, selectedAudioModelValue]);

  const creditCost = useMemo(() => {
    if (variant === TYPE_UPLOAD.PPTX) {
      return calculatePresentationCredit({
        enableAI: isAIContentEnabled,
        slideCount: pptxSlideCount,
        model: aiConfig?.model,
      });
    }
    if (isAudio) {
      // Auto-detect OFF → backend uses the basic transcription path with no
      // model hint, which is free. Skip the calculation so the "Tiêu hao …"
      // label hides too.
      if (showAutoDetectSwitch && aiConfig?.enabled === false) return 0;
      return calculateTranscriptCredit({
        durationSeconds: duration,
        model: selectedAudioModelData,
      });
    }
    return 0;
  }, [
    variant,
    duration,
    selectedAudioModelData,
    isAIContentEnabled,
    pptxSlideCount,
    aiConfig?.model,
    aiConfig?.enabled,
    showAutoDetectSwitch,
    isAudio,
  ]);

  const handleSubmit = async () => {
    if (!file || !config) return;

    // PPTX — AI narration switched on without package access → upsell, keep the switch ON
    if (isPptxAccessLocked && Boolean(aiConfig?.enabled)) {
      setOpenPopupUpPkg(true);
      return;
    }

    // AUDIO — Permission FIRST guard; only when STT runs (`enabled` — the legacy switch never updates `subtitleMode`)
    if (isAudio) {
      if (
        (!hasTranscriptPermission || !hasSelectedModelPermission) &&
        Boolean(aiConfig?.enabled)
      ) {
        setOpenPopupUpPkg(true);
        return;
      }
    }

    // Credit-locked guard:
    //   • PPTX: always charges (slide-based), so locked credits must block.
    //   • AUDIO / VIDEO_DUBBING: charges only when auto-detect-voice is ON
    //     (which sends a model). With it OFF the backend runs the free
    //     transcription path, so the locked dialog must not block the user.
    const hasProjectLocked =
      isPptx ||
      (isAudio && !(showAutoDetectSwitch && aiConfig?.enabled === false));
    if (hasProjectLocked && isCreditsLocked) {
      setOpenDialogLockedCycle(true);
      return;
    }

    // AUDIO — not enough credits guard (only when there is a credit cost).
    if (isAudio && creditCost > 0 && totalCredits < creditCost) {
      setOpenDialogNotEnoughCredits(true);
      return;
    }

    setProcessing(true);
    try {
      // SRT method: the video is uploaded as the project source, while blocks +
      // timings come from the .srt. No STT, no language/model hints.
      if (isSrtMode) {
        const videoPayload = await config.process(file);
        const srtPayload = await processFileVariant(srtFile);
        const combined = buildVideoSrtCreatePayload({
          videoPayload,
          srtPayload,
        });
        const { projectId } = await createProject({
          title: buildDefaultTitle(t),
          product: combined.product,
          blocks: combined.blocks,
          source: combined.source,
          language: combined.language,
          extra: combined.extra,
        });
        onClose?.();
        finalizeProjectDefault({ projectId, product: combined.product });
        return;
      }

      const payload = await config.process(file);

      let enrichedSource = payload.source;
      if (enrichedSource && isPptx && isAIContentEnabled) {
        enrichedSource = {
          ...enrichedSource,
          language: aiConfig.language,
          model: aiConfig.model?.value || aiConfig.model?.model,
        };
      }
      if (enrichedSource && isAudio) {
        // When the auto-detect-voice switch is OFF, the user explicitly opted
        // out of the language/model picker — submit without those hints and let
        // the backend infer. When the switch is ON (or the legacy non-switch
        // layout), forward the selection: `language: 'auto'` is normalized to
        // omitted (backend infers from audio), otherwise map through
        // `languagesV3` to align with the backend list.
        if (aiConfig?.enabled === false) {
          enrichedSource = { ...enrichedSource };
        } else {
          const lang = aiConfig?.language;
          const mappedLanguage =
            lang && lang !== 'auto'
              ? languagesV3?.find((o) => o.value === lang)?.value || lang
              : undefined;
          enrichedSource = {
            ...enrichedSource,
            ...(mappedLanguage ? { language: mappedLanguage } : {}),
            model: selectedAudioModelValue || undefined,
          };
        }

        // Only audio/video transcription variants opt into the
        // auto-generate-subtitles flag, and only when the rollout flag is on.
        // The field lives inside `source` alongside language/model so the
        // backend reads it from the same per-input payload.
        if (showAutoDetectSwitch) {
          enrichedSource = {
            ...enrichedSource,
            autoGenerateSubtitles: Boolean(aiConfig?.enabled),
          };
        }
      }

      // Empty dubbing project (auto-generate-subtitles OFF) opens with one
      // empty block so the user has somewhere to type — mirrors the TTS
      // empty-project flow. When the switch is ON the backend fills blocks
      // from the transcription result, so leave them empty.
      const firstBlocks =
        isAudio && showAutoDetectSwitch && aiConfig?.enabled === false
          ? buildEmptyDubbingBlocks()
          : payload.blocks;

      const { projectId } = await createProject({
        title: buildDefaultTitle(t),
        product: payload.product,
        blocks: firstBlocks,
        source: enrichedSource,
        language: payload.language,
        extra: payload.extra,
        sourceType: payload.sourceType,
      });

      onClose?.();
      finalizeProjectDefault({ projectId, product: payload.product });
    } catch (e) {
      // Backend `FEATURE_NOT_IN_PACKAGE` — both flavors: hook-wrapped errorCode
      // (when API returned `!status`) and raw axios error response.
      const backendErrorCode =
        e?.response?.data?.error_code ?? e?.response?.data?.errorCode;
      const errorCode = e?.errorCode ?? backendErrorCode;
      if (isAudio && errorCode === ERROR_CODE.FEATURE_NOT_IN_PACKAGE) {
        setOpenPopupUpPkg(true);
        return;
      }

      // Other errors → toast only. Don't `setError`: that piggybacks on the
      // dropzone's inline error and would leave the submit button stuck
      // disabled until the user removes the file.
      const msg =
        e?.code === 'create-failed' ? 'createProjectFailed' : 'uploadError';
      dispatch(actions.noti.push({ severity: 'error', message: msg }));
      handleRemove();
    } finally {
      setProcessing(false);
    }
  };

  if (!config) return null;

  const submitDisabled =
    !file ||
    Boolean(error) ||
    computingDetails ||
    processing ||
    (isSrtMode && (!srtFile || Boolean(srtError) || computingSrt));

  const creditCostLabel = creditCost
    ? t('costsCreditBold', { value: creditCost.toLocaleString(currentLang) })
    : '';

  // Header label above the AI options: subtitle-method dropdown (video dubbing,
  // US-03) → PPTX AI switch → auto-detect switch.
  const renderAiHeaderLabel = () => {
    if (showSubtitleMethodDropdown) return t('subtitleCreationMethod');
    if (isPptx) return t('createAIPresentationContent');
    return t('autoDetectVoice');
  };

  const renderLanguageLabel = () => {
    switch (variant) {
      case TYPE_UPLOAD.PPTX:
        return t('selectOutputLanguage');
      case TYPE_UPLOAD.VIDEO_DUBBING:
        return t('selectContentLanguageVideo');
      case TYPE_UPLOAD.AUDIO:
        return t('selectContentLanguage');
      default:
        return '';
    }
  };

  // Legacy upgrade tooltip: HTML body + inline "Buy now" link to payment page.
  const renderUpgradeTooltip = (html) => (
    <span>
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />{' '}
      <Link
        to={route.PAYMENT}
        style={{ color: '#fff', textDecoration: 'underline' }}
      >
        {t('buyNow')}
      </Link>
    </span>
  );
  const upgradeTooltip = isPptxAccessLocked
    ? renderUpgradeTooltip(
        t('descriptionUnlockPremiumAIFeaturesNow', {
          pkgName: pptxUpgradePkgName,
        }),
      )
    : null;

  // AUDIO/VIDEO_DUBBING: crown models the user's package lacks (feature name === model name).
  const isModelLocked = (m) => {
    if (!isAudio) return false;
    const key = m?.value || m?.model;
    return Boolean(key) && !userFeatures.includes(key);
  };
  const modelUpgradeDescription = (m) =>
    t('descriptionUpgradeModelNotSupported', {
      model: m?.label || m?.model || m?.value,
      pkgName:
        getMinPackageNameForFeature({
          packages,
          feature: m?.value || m?.model,
          language: currentLang,
        }) || 'VIP',
    });
  const renderModelLockedTooltip = (m) =>
    renderUpgradeTooltip(modelUpgradeDescription(m));

  return (
    <>
      <NavigationBlocker
        when={processing}
        navigate={(path) => history.push(path)}
        description={t('leaveWhileProcessingDescriptionV2')}
      />
      {(isPptx || isAudio) && openDialogLockedCycle && (
        <NotificationDialogV4
          open
          onClose={() => setOpenDialogLockedCycle(false)}
          image={LockCycle}
          title={t('charactersTemporarilyLocked')}
          description={t('descriptionPopupLockedCredit')}
          actionComponent={
            <CommonButton
              variant="contained"
              onClick={() => setOpenDialogLockedCycle(false)}
            >
              {t('understoodV3')}
            </CommonButton>
          }
        />
      )}
      {isAudio && (
        <PopupUpPkgUseRewrite
          open={openPopupUpPkg}
          onClose={() => setOpenPopupUpPkg(false)}
          pkgName={upgradePkgName || undefined}
          description={
            // Only the model is paywalled (not the whole feature) → name it.
            hasTranscriptPermission && !hasSelectedModelPermission
              ? modelUpgradeDescription(aiConfig?.model)
              : undefined
          }
          featureKey={missingFeature}
          surface={MOE_SURFACE.PROJECT_CREATE}
          product={PROJECT_PRODUCT.DUBBING}
        />
      )}
      {isPptx && (
        <PopupUpPkgUseRewrite
          open={openPopupUpPkg}
          onClose={() => setOpenPopupUpPkg(false)}
          pkgName={pptxUpgradePkgName}
          targetPackageCode={pptxTargetPackageCode}
          featureKey={FEATURE.AI_GENERATE_NARRATION}
          surface={MOE_SURFACE.PROJECT_CREATE}
          product={PROJECT_PRODUCT.PRESENTATION}
        />
      )}
      {isAudio && openDialogNotEnoughCredits && (
        <ActionDialog
          open
          onClose={() => setOpenDialogNotEnoughCredits(false)}
          title={t('checkNotEnoughCreditsTitle')}
          styleTitle={{ textAlign: 'center' }}
          contentComponent={
            <Typography
              style={{ textAlign: 'center' }}
              variant="body-16-regular"
              color={COLOR.descriptionGray}
              dangerouslySetInnerHTML={{
                __html: t('checkNotEnoughCreditsDesc'),
              }}
            />
          }
          fullWidth
          image={CrownImg}
          imageContainerSize="100px"
          width="640px"
          actionComponent={
            <CommonButton
              variant="contained"
              onClick={() => {
                setOpenDialogNotEnoughCredits(false);
                history.push(route.PAYMENT);
              }}
            >
              {t('buyNow')}
            </CommonButton>
          }
        />
      )}
      <CommonDialog
        open={open}
        onClose={onClose}
        title={t(config.titleKey)}
        width="640px"
        disableAutoClose
        disableClose={processing}
        contentComponent={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              width: '100%',
            }}
          >
            <ProjectFileDropzone
              fileType={config.fileType}
              maxSize={config.maxSize}
              maxSizeLabel={
                isAudio
                  ? t('audioFileLimit', {
                      size: formatFileSize(MAX_AUDIO_SIZE_BYTES),
                      minutes: MAX_AUDIO_DURATION_SECONDS / 60,
                    })
                  : null
              }
              file={file}
              fileDetails={fileDetails}
              fileLoading={computingDetails}
              fileLoadingText={computingDetails ? t('processing') : ''}
              onFiles={handleFiles}
              onRemove={handleRemove}
              error={error}
              errorDetail={errorDetail}
            />
            {showAIOptions && aiConfig && (
              <ProjectAIOptions
                value={aiConfig}
                onChange={setAiConfig}
                languages={languages}
                supportedLanguages={supportedLanguageLabels}
                models={models}
                isModelLocked={isModelLocked}
                renderModelLockedTooltip={renderModelLockedTooltip}
                renderModelDescription={getModelDescription}
                renderModelCredit={(m) => {
                  if (isPptx) {
                    return m?.creditPerSlide
                      ? t('creditPerSlide', { value: m.creditPerSlide })
                      : null;
                  }
                  if (isAudio) {
                    return m?.creditPerSecond
                      ? t('creditPerSecond', { value: m.creditPerSecond })
                      : null;
                  }
                  return null;
                }}
                showSwitch={
                  !showSubtitleMethodDropdown &&
                  (isPptx || showAutoDetectSwitch)
                }
                headerVariant={
                  showSubtitleMethodDropdown ? 'dropdown' : 'switch'
                }
                mode={subtitleMode}
                modeOptions={subtitleModeOptions}
                onModeChange={handleSubtitleModeChange}
                modeContent={
                  isSrtMode ? (
                    <ProjectFileDropzone
                      fileType={['srt']}
                      maxSize={SUBTITLE_SRT_MAX_SIZE_BYTES}
                      maxSizeLabel={t('subtitleFileLimit', {
                        chars:
                          SUBTITLE_MAX_TOTAL_CHARS.toLocaleString(currentLang),
                        size: formatFileSize(SUBTITLE_SRT_MAX_SIZE_BYTES),
                      })}
                      file={srtFile}
                      fileDetails={srtDetails}
                      fileLoading={computingSrt}
                      fileLoadingText={computingSrt ? t('processing') : ''}
                      onFiles={handleSrtFiles}
                      onRemove={clearSrt}
                      error={srtError}
                      errorDetail={srtErrorDetail}
                    />
                  ) : null
                }
                switchDisabled={
                  showAutoDetectSwitch &&
                  !isVideoDubbing &&
                  (!file || forceAutoDetect)
                }
                locked={isPptxAccessLocked}
                lockedTooltip={upgradeTooltip}
                aiSwitchLabel={renderAiHeaderLabel()}
                switchTooltip={
                  isPptx ? t('createAIPresentationContentTooltip') : undefined
                }
                languageLabel={renderLanguageLabel()}
                modelLabel={t('selectModel')}
                languagePlaceholder={t('selectLanguage')}
                searchPlaceholder={t('search')}
                tipText={resolveAiOptionsTipText({
                  isAudio,
                  isPptx,
                  slideV2Enabled: presentationSlideV2Enabled,
                  t,
                })}
              />
            )}
          </Box>
        }
        actionComponent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '12px',
              width: '100%',
            }}
          >
            <Typography
              variant="body-14-regular"
              color={COLOR.darkGray}
              style={{ textAlign: 'right' }}
              dangerouslySetInnerHTML={{
                __html: creditCostLabel,
              }}
            />
            <CommonButton
              variant="contained"
              bgrDisabled={COLOR.yellow.default}
              customStyle={{
                height: 40,
                padding: '8px 16px',
                borderRadius: '8px',
                minWidth: 'auto',
                '&:disabled': {
                  opacity: 0.32,
                  color: COLOR.darkBlue,
                },
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              disabled={submitDisabled}
              onClick={handleSubmit}
              startIcon={
                processing ? (
                  <CircularProgress size={16} sx={{ color: COLOR.darkBlue }} />
                ) : null
              }
            >
              {t('continue')}
            </CommonButton>
          </Box>
        }
      />
    </>
  );
};

export default UploadProjectFilePopup;
